import type { i18n as I18n } from 'i18next';

import type { NotificationItem } from '@/shared/registry/types';

/**
 * Backend modules whose screens (and therefore translations) live in a frontend module with another id
 * (`identity` → `company`), like the error-message aliases of `getErrorMessage`.
 */
const NAMESPACE_ALIASES: Readonly<Record<string, string>> = { identity: 'company' };

/** The i18n namespace / frontend module that owns a backend module's texts. */
export const namespaceOf = (module: string): string => NAMESPACE_ALIASES[module] ?? module;

/** The event part of a type: `assigned` for `tasks.assigned` (also when the module part differs). */
export function eventOf(type: string, module?: string): string {
  if (module && type.startsWith(`${module}.`)) return type.slice(module.length + 1);
  const dot = type.indexOf('.');
  return dot >= 0 ? type.slice(dot + 1) : type;
}

/**
 * The text of a notification: the emitting module's template `t("<module>:notifications.<event>", params)` (ARCHITECTURE
 * §4.7) when it exists and every placeholder it uses has a value; otherwise the server-side fallback text (a template
 * with a missing parameter would read "назначил вам задачу «»").
 */
export function renderNotificationText(
  notification: Pick<NotificationItem, 'module' | 'type' | 'params' | 'fallbackText'>,
  i18n: Pick<I18n, 't' | 'exists'>,
): string {
  const key = `${namespaceOf(notification.module)}:notifications.${eventOf(notification.type, notification.module)}`;
  if (!i18n.exists(key)) return notification.fallbackText;
  const state = { missing: false };
  const text: unknown = i18n.t(key, {
    ...notification.params,
    defaultValue: '',
    missingInterpolationHandler: () => {
      state.missing = true;
      return '';
    },
  });
  return !state.missing && typeof text === 'string' && text.trim() ? text : notification.fallbackText;
}

/** "comment-added" → "Comment added": the last resort label of a type nobody translated. */
export function humanize(event: string): string {
  const words = event.replace(/[._-]+/gu, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : event;
}

/**
 * Label of a notification type on the settings page: the emitting module's `notificationTypes.<event>`, this module's
 * own label for well-known types, else the humanized event.
 */
export function typeLabel(type: string, module: string, i18n: Pick<I18n, 't' | 'exists'>): string {
  const event = eventOf(type, module);
  const own = `${namespaceOf(module)}:notificationTypes.${event}`;
  if (i18n.exists(own)) return i18n.t(own);
  const known = `notifications:types.${type.replaceAll('.', '_')}`;
  if (i18n.exists(known)) return i18n.t(known);
  return humanize(event);
}

/** Display name of a module: this module's label for the well-known ones, else the module's navigation title. */
export function moduleLabel(module: string, i18n: Pick<I18n, 't' | 'exists'>): string {
  const own = `notifications:modules.${module}`;
  if (i18n.exists(own)) return i18n.t(own);
  const nav = `${namespaceOf(module)}:nav.title`;
  return i18n.exists(nav) ? i18n.t(nav) : humanize(module);
}
